import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/api';
import { Loader2, Search, Filter, Inbox, Building2, Clock, CheckCircle2, XCircle, AlertCircle, MessageSquare, ChevronRight, Eye, Send, MoreHorizontal, Tag, Shield, AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const STATUS_COLORS = {
  DRAFT: 'text-[var(--text-muted)] bg-[var(--surface)] border-[var(--border)]',
  LINK_SENT: 'text-blue-400 bg-blue-900/20 border-blue-800/50',
  SUBMITTED: 'text-[var(--warning)] bg-[var(--warning-muted)] border-[var(--warning)]',
  NEEDS_INFO: 'text-orange-400 bg-orange-900/20 border-orange-800/50',
  ACCOUNTING_REVIEW: 'text-purple-400 bg-purple-900/20 border-purple-800/50',
  APPROVED: 'text-[var(--success)] bg-[var(--success-muted)] border-[var(--success)]',
  DECLINED: 'text-[var(--error)] bg-[var(--error-muted)] border-[var(--error)]',
};

const SOURCE_BADGES = {
  SELF_SERVE: { label: 'Self-Serve', color: 'text-teal-400 bg-teal-900/20 border-teal-800/50' },
  SALES_INVITE: { label: 'Sales Invite', color: 'text-blue-400 bg-blue-900/20 border-blue-800/50' },
};

const TENANT_BADGES = {
  MM: { label: 'MM', color: 'text-emerald-400 bg-emerald-900/20 border-emerald-700/50', fullName: 'Millennials Maintenance' },
  MT: { label: 'MT', color: 'text-violet-400 bg-violet-900/20 border-violet-700/50', fullName: 'MT Tenant' },
  UNASSIGNED: { label: 'UNASSIGNED', color: 'text-amber-400 bg-amber-900/30 border-amber-600/50 animate-pulse', fullName: 'Needs Assignment' },
};

const STATUSES = ['DRAFT', 'LINK_SENT', 'SUBMITTED', 'NEEDS_INFO', 'ACCOUNTING_REVIEW', 'APPROVED', 'DECLINED'];

export default function IntakeInbox() {
  const navigate = useNavigate();
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showInfoRequest, setShowInfoRequest] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Tenant assignment state
  const [showTenantAssign, setShowTenantAssign] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [tenantAssignLoading, setTenantAssignLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => { 
    load(); 
    loadCurrentUser();
  }, [search, statusFilter, sourceFilter, tenantFilter]);

  const loadCurrentUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setCurrentUser(res.data);
    } catch (e) {
      console.error('Failed to load user');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (tenantFilter) params.tenant_group = tenantFilter;
      const res = await api.get('/intake', { params });
      setIntakes(res.data);
    } catch (e) {
      toast.error('Failed to load intake applications');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (intake) => {
    setDetailLoading(true);
    setShowDetail(true);
    try {
      const res = await api.get(`/intake/${intake.id}`);
      setSelectedIntake(res.data);
    } catch (e) {
      toast.error('Failed to load details');
      setShowDetail(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAssignTenant = async (tenantGroup) => {
    if (!selectedIntake) return;
    
    // Check if already assigned (only master_admin can change)
    const currentTenant = selectedIntake.tenant_group;
    if (currentTenant && currentTenant !== 'UNASSIGNED') {
      if (currentUser?.role !== 'master_admin') {
        toast.error('Only Master Admin can change tenant assignment');
        return;
      }
    }
    
    setTenantAssignLoading(true);
    try {
      const res = await api.post(`/intake/${selectedIntake.id}/assign-tenant`, { tenant_group: tenantGroup });
      toast.success(`Assigned to ${tenantGroup}`);
      
      // Update local state
      setSelectedIntake(prev => ({ ...prev, tenant_group: tenantGroup }));
      load(); // Refresh list
      setShowTenantAssign(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assignment failed');
    } finally {
      setTenantAssignLoading(false);
    }
  };

  const handleApprove = async () => {
    // Check tenant assignment first
    if (!selectedIntake.tenant_group || selectedIntake.tenant_group === 'UNASSIGNED') {
      toast.error('Must assign to MM or MT before approving');
      setShowTenantAssign(true);
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await api.post(`/intake/${selectedIntake.id}/approve`);
      toast.success(res.data.message);
      if (res.data.billing_link_sent_to) {
        toast.success(`Billing setup link emailed to ${res.data.billing_link_sent_to}`, { duration: 6000 });
      }
      setShowDetail(false);
      // Take user straight to the new lead's Pipeline detail so they can email contract / mark signed
      if (res.data.customer_id) {
        toast.info('Lead moved to Pipeline — finish the contract step there', { duration: 5000 });
        setTimeout(() => navigate(`/ops/customers/${res.data.customer_id}`), 600);
      } else {
        load();
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Approval failed';
      if (detail.includes('TENANT_ASSIGNMENT_REQUIRED')) {
        toast.error('Must assign to MM or MT before approving');
        setShowTenantAssign(true);
      } else {
        toast.error(detail);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!infoMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/intake/${selectedIntake.id}/request-info`, { message: infoMessage });
      toast.success('Info request sent to applicant');
      setShowInfoRequest(false);
      setInfoMessage('');
      setShowDetail(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    setActionLoading(true);
    try {
      await api.post(`/intake/${selectedIntake.id}/decline`, { reason: declineReason });
      toast.success('Application declined');
      setShowDecline(false);
      setDeclineReason('');
      setShowDetail(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/intake/${deleteTarget.id}`);
      toast.success('Application deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/intake/${selectedIntake.id}/add-note`, { note: noteText });
      toast.success('Note added');
      setNoteText('');
      // Refresh details
      const res = await api.get(`/intake/${selectedIntake.id}`);
      setSelectedIntake(res.data);
    } catch (e) {
      toast.error('Failed to add note');
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

  const isMasterAdmin = currentUser?.role === 'master_admin';
  const canChangeTenant = (intake) => {
    if (!intake) return false;
    const tenant = intake.tenant_group;
    // Can assign if UNASSIGNED, or if master_admin
    return !tenant || tenant === 'UNASSIGNED' || isMasterAdmin;
  };

  const stats = {
    submitted: intakes.filter(i => i.status === 'SUBMITTED').length,
    needsInfo: intakes.filter(i => i.status === 'NEEDS_INFO').length,
    unassigned: intakes.filter(i => !i.tenant_group || i.tenant_group === 'UNASSIGNED').length,
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="intake-inbox-page">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// CUSTOMER ONBOARDING</p>
          <h1 className="font-['Manrope'] font-extrabold text-2xl" style={{ color: 'var(--text-primary)' }}>Intake Inbox</h1>
          <p className="font-['IBM_Plex_Sans'] text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{intakes.length} applications</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Awaiting Review', value: stats.submitted, icon: Clock, color: 'var(--warning)' },
          { label: 'Needs Info', value: stats.needsInfo, icon: AlertCircle, color: 'rgb(251, 146, 60)' },
          { label: 'Needs Tenant', value: stats.unassigned, icon: AlertTriangle, color: 'rgb(251, 191, 36)', alert: stats.unassigned > 0 },
        ].map(({ label, value, icon: Icon, color, alert }) => (
          <div key={label} className={`card-slate p-4 ${alert ? 'ring-2 ring-amber-500/50' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>{label}</span>
              <Icon size={14} style={{ color }} className={alert ? 'animate-pulse' : ''} />
            </div>
            <p className="text-2xl font-['JetBrains_Mono'] font-bold" style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-faint)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search company, email..."
            className="w-full input-slate pl-9" data-testid="intake-search" />
        </div>
        <div>
          <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Tenant</label>
          <select value={tenantFilter} onChange={e => setTenantFilter(e.target.value)} className="select-slate min-w-[120px]" data-testid="tenant-filter">
            <option value="">All</option>
            <option value="MM">MM Only</option>
            <option value="MT">MT Only</option>
            <option value="UNASSIGNED">Unassigned</option>
          </select>
        </div>
        <div>
          <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="select-slate min-w-[140px]">
            <option value="">All</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Source</label>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="select-slate min-w-[140px]">
            <option value="">All</option>
            <option value="SELF_SERVE">Self-Serve</option>
            <option value="SALES_INVITE">Sales Invite</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card-slate overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div>
        ) : intakes.length === 0 ? (
          <div className="py-16 text-center">
            <Inbox size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
            <p className="font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>No applications match your filters</p>
          </div>
        ) : (
          <table className="w-full table-comfortable" data-testid="intake-table">
            <thead>
              <tr>
                {['Tenant', 'Company', 'Contact', 'Source', 'Status', 'Trucks', 'Credit', 'Submitted', ''].map(h => (
                  <th key={h} className={['Credit', 'Trucks'].includes(h) ? 'hidden lg:table-cell' : ''}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {intakes.map(intake => {
                const tenantBadge = TENANT_BADGES[intake.tenant_group] || TENANT_BADGES.UNASSIGNED;
                return (
                  <tr key={intake.id} className="cursor-pointer" onClick={() => openDetail(intake)} data-testid={`intake-row-${intake.id}`}>
                    <td>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${tenantBadge.color}`}
                        data-testid={`tenant-badge-${intake.id}`}>
                        {tenantBadge.label}
                      </span>
                    </td>
                    <td className="font-['Manrope'] font-bold" style={{ color: 'var(--text-primary)' }}>
                      {intake.company_name || <span style={{ color: 'var(--text-faint)' }}>Draft</span>}
                    </td>
                    <td className="text-sm" style={{ color: 'var(--text-muted)' }}>{intake.contact_email || '—'}</td>
                    <td>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${SOURCE_BADGES[intake.source]?.color || ''}`}>
                        {SOURCE_BADGES[intake.source]?.label || intake.source}
                      </span>
                    </td>
                    <td>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${STATUS_COLORS[intake.status] || ''}`}>
                        {intake.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="font-['JetBrains_Mono'] text-sm hidden lg:table-cell" style={{ color: 'var(--text-muted)' }}>{intake.num_trucks || '—'}</td>
                    <td className="font-['JetBrains_Mono'] text-sm hidden lg:table-cell" style={{ color: 'var(--text-muted)' }}>
                      {intake.credit_requested ? `$${Number(intake.credit_requested).toLocaleString()}` : '—'}
                    </td>
                    <td className="text-xs" style={{ color: 'var(--text-faint)' }}>{fmt(intake.submitted_at || intake.created_at)}</td>
                    <td>
                      <ChevronRight size={14} style={{ color: 'var(--text-faint)' }} />
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <button onClick={() => setDeleteTarget({ id: intake.id, name: intake.company_name || 'Draft' })}
                        data-testid={`delete-intake-${intake.id}`}
                        title="Delete application"
                        className="p-1.5 rounded transition-colors"
                        style={{ color: 'var(--text-faint)' }}
                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--error)'; e.currentTarget.style.background = 'var(--error-muted)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; e.currentTarget.style.background = 'transparent'; }}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          {detailLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div>
          ) : selectedIntake && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="font-['Manrope']" style={{ color: 'var(--text-primary)' }}>
                    {selectedIntake.form_data?.business_info?.legal_business_name || 'Draft Application'}
                  </DialogTitle>
                  <div className="flex gap-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${TENANT_BADGES[selectedIntake.tenant_group]?.color || TENANT_BADGES.UNASSIGNED.color}`}>
                      {TENANT_BADGES[selectedIntake.tenant_group]?.label || 'UNASSIGNED'}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${SOURCE_BADGES[selectedIntake.source]?.color || ''}`}>
                      {SOURCE_BADGES[selectedIntake.source]?.label}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${STATUS_COLORS[selectedIntake.status] || ''}`}>
                      {selectedIntake.status?.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              </DialogHeader>

              {/* Tenant Assignment Alert - Show when UNASSIGNED */}
              {(!selectedIntake.tenant_group || selectedIntake.tenant_group === 'UNASSIGNED') && (
                <div className="mt-4 p-4 rounded-lg border-2 border-dashed" style={{ borderColor: 'rgb(251, 191, 36)', background: 'rgba(251, 191, 36, 0.1)' }}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="mt-0.5 flex-shrink-0" style={{ color: 'rgb(251, 191, 36)' }} />
                    <div className="flex-1">
                      <p className="font-['Manrope'] font-bold text-sm mb-1" style={{ color: 'rgb(251, 191, 36)' }}>
                        Tenant Assignment Required
                      </p>
                      <p className="text-xs font-['IBM_Plex_Sans'] mb-3" style={{ color: 'var(--text-muted)' }}>
                        This application must be routed to MM or MT before it can be approved.
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleAssignTenant('MM')} 
                          disabled={tenantAssignLoading}
                          className="text-xs"
                          style={{ background: 'rgb(16, 185, 129)', color: 'white' }}
                          data-testid="assign-mm-btn"
                        >
                          {tenantAssignLoading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Tag size={12} className="mr-1" />}
                          Assign to MM
                        </Button>
                        <Button 
                          onClick={() => handleAssignTenant('MT')} 
                          disabled={tenantAssignLoading}
                          className="text-xs"
                          style={{ background: 'rgb(139, 92, 246)', color: 'white' }}
                          data-testid="assign-mt-btn"
                        >
                          {tenantAssignLoading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Tag size={12} className="mr-1" />}
                          Assign to MT
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tenant Info (when assigned) */}
              {selectedIntake.tenant_group && selectedIntake.tenant_group !== 'UNASSIGNED' && (
                <div className="mt-4 p-3 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield size={14} style={{ color: 'var(--success)' }} />
                      <span className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>
                        Assigned to <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{TENANT_BADGES[selectedIntake.tenant_group]?.fullName}</span>
                      </span>
                      {selectedIntake.tenant_assigned_at && (
                        <span className="text-[10px] font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>
                          on {fmt(selectedIntake.tenant_assigned_at)}
                        </span>
                      )}
                    </div>
                    {isMasterAdmin && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setShowTenantAssign(true)}
                        className="text-xs h-7"
                        style={{ color: 'var(--text-faint)' }}
                        data-testid="change-tenant-btn"
                      >
                        Change
                      </Button>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-6 mt-4">
                {/* Business Info */}
                <Section title="Business Information">
                  <InfoGrid data={[
                    ['Legal Name', selectedIntake.form_data?.business_info?.legal_business_name],
                    ['DBA', selectedIntake.form_data?.business_info?.trade_names_dba],
                    ['Email', selectedIntake.form_data?.business_info?.email],
                    ['Phone', selectedIntake.form_data?.business_info?.phone],
                    ['Address', `${selectedIntake.form_data?.business_info?.street_address || ''}, ${selectedIntake.form_data?.business_info?.city || ''}, ${selectedIntake.form_data?.business_info?.state || ''} ${selectedIntake.form_data?.business_info?.zip_code || ''}`],
                    ['DOT #', selectedIntake.form_data?.business_info?.dot_number],
                    ['FEIN #', selectedIntake.form_data?.business_info?.fein_number],
                    ['Business Type', selectedIntake.form_data?.business_info?.business_type],
                    ['Years in Business', selectedIntake.form_data?.business_info?.years_in_business],
                    ['# Trucks', selectedIntake.form_data?.business_info?.num_trucks],
                    ['# Trailers', selectedIntake.form_data?.business_info?.num_trailers],
                    ['Credit Requested', selectedIntake.form_data?.business_info?.credit_amount_requested ? `$${Number(selectedIntake.form_data.business_info.credit_amount_requested).toLocaleString()}` : '—'],
                  ]} />
                </Section>

                {/* Principal Owner */}
                <Section title="Principal Owner">
                  <InfoGrid data={[
                    ['Name', selectedIntake.form_data?.principal_owner?.name],
                    ['Title', selectedIntake.form_data?.principal_owner?.title],
                    ['Email', selectedIntake.form_data?.principal_owner?.email],
                    ['Phone', selectedIntake.form_data?.principal_owner?.home_phone],
                    ['SSN Last 4', selectedIntake.form_data?.principal_owner?.ssn_last4 ? `****${selectedIntake.form_data.principal_owner.ssn_last4}` : '—'],
                  ]} />
                </Section>

                {/* Bank Reference */}
                <Section title="Bank Reference">
                  <InfoGrid data={[
                    ['Bank', selectedIntake.form_data?.bank_reference?.bank_name],
                    ['Contact', selectedIntake.form_data?.bank_reference?.contact_name],
                    ['Phone', selectedIntake.form_data?.bank_reference?.phone],
                    ['Account Last 4', selectedIntake.form_data?.bank_reference?.account_number_last4 ? `****${selectedIntake.form_data.bank_reference.account_number_last4}` : '—'],
                  ]} />
                </Section>

                {/* Credit References */}
                <Section title="Credit References">
                  <div className="space-y-3">
                    {selectedIntake.form_data?.credit_references?.map((ref, i) => (
                      <div key={i} className="p-3 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                        <p className="text-xs font-['JetBrains_Mono'] font-bold mb-2" style={{ color: 'var(--primary)' }}>Reference #{i + 1}</p>
                        <InfoGrid data={[
                          ['Company', ref.name],
                          ['Contact', ref.phone_email],
                          ['Account #', ref.account_number],
                        ]} compact />
                      </div>
                    ))}
                  </div>
                </Section>

                {/* Signature */}
                <Section title="Authorization">
                  <InfoGrid data={[
                    ['Signed By', selectedIntake.form_data?.signature?.printed_name],
                    ['Title', selectedIntake.form_data?.signature?.title],
                    ['Date', selectedIntake.form_data?.signature?.date],
                    ['Terms Accepted', selectedIntake.form_data?.signature?.agreed_to_terms ? 'Yes' : 'No'],
                  ]} />
                </Section>

                {/* Tenant Assignment Audit Trail */}
                {selectedIntake.tenant_assignments?.length > 0 && (
                  <Section title="Tenant Assignment History">
                    <div className="space-y-2">
                      {selectedIntake.tenant_assignments.map((a, i) => (
                        <div key={i} className="flex items-center gap-3 text-xs p-2 rounded" style={{ background: 'var(--bg)' }}>
                          <Tag size={12} style={{ color: 'var(--text-faint)' }} />
                          <span style={{ color: 'var(--text-muted)' }}>
                            {a.from_tenant || 'UNASSIGNED'} → <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{a.to_tenant}</span>
                          </span>
                          <span style={{ color: 'var(--text-faint)' }}>by {a.assigned_by_name}</span>
                          <span className="font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>{fmt(a.assigned_at)}</span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Notes */}
                <Section title="Internal Notes">
                  <div className="space-y-3">
                    {selectedIntake.notes?.length > 0 ? selectedIntake.notes.map(note => (
                      <div key={note.id} className="p-3 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold font-['IBM_Plex_Sans']" style={{ color: 'var(--primary)' }}>{note.author}</span>
                          <span className="text-[10px] font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>{fmt(note.created_at)}</span>
                        </div>
                        <p className="text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>{note.text}</p>
                      </div>
                    )) : (
                      <p className="text-sm" style={{ color: 'var(--text-faint)' }}>No notes yet</p>
                    )}
                    <div className="flex gap-2">
                      <input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..."
                        className="flex-1 input-slate" />
                      <button onClick={handleAddNote} className="px-4 py-2 rounded-md text-sm font-['IBM_Plex_Sans']"
                        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                        Add
                      </button>
                    </div>
                  </div>
                </Section>
              </div>

              {/* Actions */}
              {['SUBMITTED', 'ACCOUNTING_REVIEW'].includes(selectedIntake.status) && (
                <DialogFooter className="gap-2 mt-6 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                  <Button variant="outline" onClick={() => setShowDecline(true)}
                    style={{ borderColor: 'var(--error)', color: 'var(--error)' }}>
                    <XCircle size={14} className="mr-2" /> Decline
                  </Button>
                  <Button variant="outline" onClick={() => setShowInfoRequest(true)}
                    style={{ borderColor: 'var(--warning)', color: 'var(--warning)' }}>
                    <MessageSquare size={14} className="mr-2" /> Request Info
                  </Button>
                  <Button onClick={handleApprove} disabled={actionLoading}
                    style={{ background: 'var(--success)', color: 'white' }}
                    data-testid="approve-intake-btn">
                    {actionLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : <CheckCircle2 size={14} className="mr-2" />}
                    Approve & Create Customer
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Tenant Change Dialog (Master Admin only) */}
      <Dialog open={showTenantAssign} onOpenChange={setShowTenantAssign}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)' }}>Change Tenant Assignment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm font-['IBM_Plex_Sans'] mb-4" style={{ color: 'var(--text-muted)' }}>
              Select which tenant this application should be assigned to. This action is audited.
            </p>
            <div className="flex gap-3">
              <Button 
                onClick={() => handleAssignTenant('MM')} 
                disabled={tenantAssignLoading || selectedIntake?.tenant_group === 'MM'}
                className="flex-1"
                style={{ background: selectedIntake?.tenant_group === 'MM' ? 'var(--border)' : 'rgb(16, 185, 129)', color: 'white' }}
              >
                {tenantAssignLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                Assign to MM
              </Button>
              <Button 
                onClick={() => handleAssignTenant('MT')} 
                disabled={tenantAssignLoading || selectedIntake?.tenant_group === 'MT'}
                className="flex-1"
                style={{ background: selectedIntake?.tenant_group === 'MT' ? 'var(--border)' : 'rgb(139, 92, 246)', color: 'white' }}
              >
                {tenantAssignLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                Assign to MT
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTenantAssign(false)} style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Info Dialog */}
      <Dialog open={showInfoRequest} onOpenChange={setShowInfoRequest}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--text-primary)' }}>Request More Information</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>
              Message to Applicant
            </label>
            <textarea value={infoMessage} onChange={e => setInfoMessage(e.target.value)} rows={4} placeholder="Please provide additional information about..."
              className="w-full input-slate resize-none" />
            <p className="text-xs mt-2" style={{ color: 'var(--text-faint)' }}>
              The applicant will receive an email with a link to update their application.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInfoRequest(false)} style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Cancel</Button>
            <Button onClick={handleRequestInfo} disabled={actionLoading} style={{ background: 'var(--warning)', color: 'white' }}>
              {actionLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={showDecline} onOpenChange={setShowDecline}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--error)' }}>Decline Application</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>
              Reason (optional)
            </label>
            <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} rows={3} placeholder="Reason for declining..."
              className="w-full input-slate resize-none" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecline(false)} style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Cancel</Button>
            <Button onClick={handleDecline} disabled={actionLoading} style={{ background: 'var(--error)', color: 'white' }}>
              {actionLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : <XCircle size={14} className="mr-2" />}
              Decline Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--error)' }} className="font-['Manrope']">Delete Application</DialogTitle>
          </DialogHeader>
          <p className="text-sm font-['IBM_Plex_Sans'] mt-2" style={{ color: 'var(--text-muted)' }}>
            Are you sure you want to delete application for <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{deleteTarget?.name}</span>? This cannot be undone.
          </p>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} style={{ background: 'var(--error)', color: 'white' }} data-testid="confirm-delete-intake">
              {deleting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div>
      <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-3" style={{ color: 'var(--text-faint)' }}>// {title}</p>
      {children}
    </div>
  );
}

function InfoGrid({ data, compact = false }) {
  return (
    <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3'} gap-3`}>
      {data.map(([label, value], i) => (
        <div key={i}>
          <p className="text-[10px] uppercase tracking-wider font-['IBM_Plex_Sans'] mb-0.5" style={{ color: 'var(--text-faint)' }}>{label}</p>
          <p className="text-sm font-['IBM_Plex_Sans']" style={{ color: value ? 'var(--text-primary)' : 'var(--text-faint)' }}>{value || '—'}</p>
        </div>
      ))}
    </div>
  );
}
